import { getCurrentSession } from "./cognito";

const API_BASE_URL =
	process.env.NEXT_PUBLIC_API_BASE_URL || "https://api.taxlienvest.com";

/**
 * Get the access token for API requests
 */
const getAccessToken = async (): Promise<string | null> => {
	try {
		const session = await getCurrentSession();
		const accessToken = session.getAccessToken();
		return accessToken.getJwtToken();
	} catch (error) {
		console.error("Failed to get access token:", error);
		return null;
	}
};

export type ExportType = "Propstream" | "GoogleMaps" | "Raw";
export type FileFormat = "csv" | "xlsx";

export interface InitiateExportRequest {
	exportType: ExportType;
	fileFormat?: FileFormat;
}

export interface InitiateExportResponse {
	exportId: string;
	uploadUrl: string;
	expiresAt: string;
	s3Path: string;
}

export interface ExportRecord {
	id: string;
	userId: string;
	exportType: ExportType;
	fileFormat: FileFormat;
	s3Path: string;
	s3Bucket: string;
	fileName: string;
	fileSize: number;
	generatedAt: string;
	writtenBy: string;
	status: "pending" | "uploading" | "completed" | "failed";
	downloadUrlExpiresAt: string | null;
	createdAt: string;
	updatedAt: string;
}

export interface DownloadUrlResponse {
	downloadUrl: string;
	expiresAt: string;
}

/**
 * Initiate an export - creates export record and returns upload URL
 */
export const initiateExport = async (
	exportType: ExportType,
	fileFormat?: FileFormat
): Promise<InitiateExportResponse> => {
	const token = await getAccessToken();
	if (!token) {
		throw new Error("Not authenticated");
	}

	const response = await fetch(`${API_BASE_URL}/exports/initiate`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${token}`,
		},
		body: JSON.stringify({ exportType, fileFormat }),
	});

	if (!response.ok) {
		const errorData = await response.json().catch(() => ({}));
		throw new Error(
			errorData.message ||
				`Failed to initiate export: ${response.statusText}`
		);
	}

	return await response.json();
};

/**
 * Upload file data to S3 using pre-signed URL
 */
export const uploadFileToS3 = async (
	uploadUrl: string,
	fileData: Blob | string,
	contentType: string
): Promise<number> => {
	const blob =
		typeof fileData === "string"
			? new Blob([fileData], { type: contentType })
			: fileData;

	const response = await fetch(uploadUrl, {
		method: "PUT",
		headers: {
			"Content-Type": contentType,
		},
		body: blob,
	});

	if (!response.ok) {
		throw new Error(`Failed to upload file to S3: ${response.statusText}`);
	}

	return blob.size;
};

/**
 * Mark export as complete after successful upload
 */
export const markExportComplete = async (
	exportId: string,
	fileSize: number
): Promise<ExportRecord> => {
	const token = await getAccessToken();
	if (!token) {
		throw new Error("Not authenticated");
	}

	const response = await fetch(
		`${API_BASE_URL}/exports/${exportId}/complete`,
		{
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${token}`,
			},
			body: JSON.stringify({ fileSize }),
		}
	);

	if (!response.ok) {
		const errorData = await response.json().catch(() => ({}));
		throw new Error(
			errorData.message ||
				`Failed to mark export as complete: ${response.statusText}`
		);
	}

	return await response.json();
};

/**
 * Get all exports for the current user
 */
export const getAllExports = async (): Promise<ExportRecord[]> => {
	const token = await getAccessToken();
	if (!token) {
		throw new Error("Not authenticated");
	}

	const response = await fetch(`${API_BASE_URL}/exports`, {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});

	if (!response.ok) {
		const errorData = await response.json().catch(() => ({}));
		throw new Error(
			errorData.message || `Failed to get exports: ${response.statusText}`
		);
	}

	return await response.json();
};

/**
 * Get export details by ID
 */
export const getExportById = async (
	exportId: string
): Promise<ExportRecord> => {
	const token = await getAccessToken();
	if (!token) {
		throw new Error("Not authenticated");
	}

	const response = await fetch(`${API_BASE_URL}/exports/${exportId}`, {
		headers: {
			Authorization: `Bearer ${token}`,
		},
	});

	if (!response.ok) {
		const errorData = await response.json().catch(() => ({}));
		throw new Error(
			errorData.message || `Failed to get export: ${response.statusText}`
		);
	}

	return await response.json();
};

/**
 * Generate download URL for an export
 */
export const generateDownloadUrl = async (
	exportId: string,
	expiresIn: number = 3600
): Promise<DownloadUrlResponse> => {
	const token = await getAccessToken();
	if (!token) {
		throw new Error("Not authenticated");
	}

	const response = await fetch(
		`${API_BASE_URL}/exports/${exportId}/download-url`,
		{
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${token}`,
			},
			body: JSON.stringify({ expiresIn }),
		}
	);

	if (!response.ok) {
		const errorData = await response.json().catch(() => ({}));
		throw new Error(
			errorData.message ||
				`Failed to generate download URL: ${response.statusText}`
		);
	}

	return await response.json();
};

/**
 * Complete export workflow: initiate, upload, and mark as complete
 */
export const exportToS3 = async (
	exportType: ExportType,
	fileData: Blob | string,
	fileFormat?: FileFormat
): Promise<ExportRecord> => {
	try {
		// Step 1: Initiate export
		const { exportId, uploadUrl } = await initiateExport(
			exportType,
			fileFormat
		);

		// Step 2: Determine content type
		const contentType =
			exportType === "Raw"
				? "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
				: "text/csv";

		// Step 3: Upload to S3
		const fileSize = await uploadFileToS3(uploadUrl, fileData, contentType);

		// Step 4: Mark as complete
		const completedExport = await markExportComplete(exportId, fileSize);

		return completedExport;
	} catch (error) {
		console.error("Export to S3 failed:", error);
		throw error;
	}
};

/**
 * Download file from URL and trigger browser download
 */
export const downloadFile = async (
	url: string,
	fileName: string
): Promise<void> => {
	try {
		const response = await fetch(url);
		if (!response.ok) {
			throw new Error(`Failed to download file: ${response.statusText}`);
		}

		const blob = await response.blob();
		const downloadUrl = window.URL.createObjectURL(blob);
		const link = document.createElement("a");
		link.href = downloadUrl;
		link.download = fileName;
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
		window.URL.revokeObjectURL(downloadUrl);
	} catch (error) {
		console.error("Download failed:", error);
		throw error;
	}
};
